import React, { useMemo } from 'react'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { SocialMenu } from '@fs/zion-social'
import { H1, Separator, Image, usePageWidth, Subheading } from '@fs/zion-ui'
import { merge } from 'lodash-es'

import SurnameDefinition from './SurnameDefinition'
import { boldFirstPart } from './utils'
import { useSurname } from '../../../../hooks/use-surname'

const sectionCss = css`
  position: relative;
  overflow: hidden;
  min-height: 624px;
  display: flex;
  align-items: center;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  .section-inner-container {
    width: 100%;
    max-width: calc(1200px + 2 * 24px);
    margin: 0 auto;
    padding: 0 24px;
  }

  .section-inner {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1200px; /* lg maxWidth equivalent */
    display: flex;
    align-items: center;
    min-height: inherit;
  }

  .content-column {
    position: relative;
    z-index: 2;
    flex: 1;
    max-width: 49%;
  }

  .background-image {
    position: absolute;
    top: 0;
    inset-inline-end: 0;
    width: 48vw;
    height: 100%;
    z-index: 0;
  }

  &[data-is-mobile] {
    background-position: bottom center;

    .section-inner-container {
      padding-top: 48px;
    }

    .content-column {
      max-width: 100%;
    }

    .background-image {
      position: relative;
      width: 100vw;
      height: auto;
      margin-inline-start: -24px;
      margin-inline-end: -24px;
    }
  }
`

const defaultPlatforms = ['facebook', 'twitter', 'whatsApp', 'copyLink']

const defaultSocialShareProps = {
  product: 'apac',
  analytics: {
    link_name: 'button: share-surname: click',
    link_page: globalThis?.location?.href || '',
    link_app: 'APAC',
  },
  sharable: null,
  billboardButtonColor: 'gray',
  buttonEmphasis: 'low',
}

export default function LeadSectionSurname({
  surname,
  surnameDisplay,
  definition, // if you want to use your own definition instead of the fetched one
  subtitle,
  images = [],
  mobileImages = [],
  bgImage,
  bgMobileImage,
  color,
  socialShareProps = defaultSocialShareProps,
}) {
  const { data, isLoading } = useSurname(surname)
  const atWidth = usePageWidth()
  const isMobile = atWidth({ default: true, lg: false })
  const selectedImage = useMemo(() => {
    const imageArray = isMobile && mobileImages.length > 0 ? mobileImages : images
    return imageArray && imageArray.length > 0 ? imageArray[Math.floor(Math.random() * imageArray.length)] : null
  }, [images, mobileImages, isMobile])
  const definitionText = definition || boldFirstPart(data?.htmlDefinition || '')
  const socialMenuProps = merge({}, defaultSocialShareProps, socialShareProps, {
    platforms: socialShareProps.onDownloadClick ? [...defaultPlatforms, 'download'] : defaultPlatforms,
  })

  return (
    <section
      css={sectionCss}
      style={{
        backgroundColor: color,
        backgroundImage: `url(${isMobile && bgMobileImage ? bgMobileImage : bgImage})`,
      }}
      data-is-mobile={boolAttr(isMobile)}
      data-testid="lead-section-surname"
    >
      <div className="section-inner-container">
        <div className="section-inner">
          <div className="content-column">
            <H1 supplementary>
              {surnameDisplay || surname}
              {subtitle && <Subheading>{subtitle}</Subheading>}
            </H1>
            <Separator size="sm" />
            <SurnameDefinition definition={definitionText} isLoading={isLoading} />
            {socialShareProps?.sharable && (
              <>
                <Separator size="md" />
                <SocialMenu {...socialMenuProps} />
              </>
            )}
            {isMobile && selectedImage && (
              <>
                <Separator size="lg" />
                <div className="background-image" data-testid="background-image">
                  <Image src={selectedImage} alt="" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {selectedImage && !isMobile && (
        <div className="background-image" data-testid="background-image">
          <Image
            src={selectedImage}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'left center',
            }}
          />
        </div>
      )}
    </section>
  )
}
